import { useEffect, useMemo } from 'react';
import { Accordion } from 'components/Accordion';
import { useAccordion } from 'hooks';
import { formatStatusForDisplay } from 'types/contact.type';
import { groupStatusHistoryByWorkflow, ProcessedStatusHistory } from '../../ContactManager.utils';

import styles from '../../ContactManager.module.scss';

const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const formatTime = (date: Date): string => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export const TabStatusHistory = ({
    statusHistory,
    workflowPartyStages = {},
}: {
    statusHistory: ProcessedStatusHistory[];
    workflowPartyStages?: Record<string, string>;
}) => {
    const workflowStatusGroups = useMemo(
        () => groupStatusHistoryByWorkflow(statusHistory, workflowPartyStages),
        [statusHistory, workflowPartyStages]
    );
    const firstWorkflowId = workflowStatusGroups[0]?.workflowId;

    const accordion = useAccordion({
        allowMultiple: true,
        defaultOpen: firstWorkflowId ? [firstWorkflowId] : [],
    });

    useEffect(() => {
        if (firstWorkflowId) {
            accordion.closeAll();
            accordion.openSection(firstWorkflowId);
        } else {
            accordion.closeAll();
        }
    }, [statusHistory, firstWorkflowId, accordion.closeAll, accordion.openSection]);

    return (
        <div className={styles.callsSection}>
            <h3>Status Change History</h3>

            {statusHistory.length > 0 ? (
                <div className={styles.workflowHistoryGroups}>
                    {workflowStatusGroups.map((group) => (
                        <Accordion
                            key={group.workflowId}
                            className={styles.workflowAccordion}
                            isOpen={accordion.isOpen(group.workflowId)}
                            onToggle={() => accordion.toggleSection(group.workflowId)}
                            header={
                                <div className={styles.workflowAccordionHeader}>
                                    <span className={styles.workflowAccordionTitle}>{group.workflowName}</span>
                                    {group.partyStage && (
                                        <span className={styles.workflowAccordionSubtitle}>
                                            Party Status: {formatStatusForDisplay(group.partyStage)}
                                        </span>
                                    )}
                                </div>
                            }
                        >
                            <div className={styles.callsTimeline}>
                                {group.items.map((item, index) => (
                                    <div className={styles.timelineItem} key={item.id || index}>
                                        <div className={styles.timelineDate}>
                                            {formatDate(item.date)}
                                            <div className={styles.callTime}>{formatTime(item.date)}</div>
                                        </div>

                                        <div className={styles.timelineContent}>
                                            <div className={styles.timelineHeader}>
                                                Status changed to <strong>{item.status}</strong>
                                            </div>
                                            {item.note && <div className='mt-xs'>Note: {item.note}</div>}
                                            <div className={`${styles.timelineUser} mt-sm`}>by {item.changedBy}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Accordion>
                    ))}
                </div>
            ) : (
                <p className={styles.noCallsMessage}>No status history available for this contact.</p>
            )}
        </div>
    );
};
